import { queryOptions, useQuery } from "@tanstack/react-query";
import { doFetch } from "@/util/fetch";
import { apiServer } from "@/util/apiServer";
import { QueryConfig } from "@/lib/react-query";

export type Feature = "ShowName" | "Signup" | "BuyPanels";

export const hasFeature = (
  features?: Array<Feature>,
  feature?: Feature | Array<Feature>
) => {
  if (!features) {
    return false;
  }

  if (!feature) {
    return true;
  }

  if (Array.isArray(feature)) {
    const intersection = features.filter((f) => feature.includes(f));

    return intersection.length !== 0;
  } else {
    return features.includes(feature);
  }
};

export type Environment = {
  awsExports: any;
  defaults: {
    panels: string;
  };
  paypal: {
    clientId: string;
  };
  stripe: {
    id: string;
  };
  google: {
    apiKey: string;
  };
  ipgeolocation: {
    url: string;
  };
  features?: Array<Feature>;
  hasFeature: (feature: Feature | Array<Feature> | undefined) => boolean;
};

export function getEnvironmentConfigQueryOptions() {
  return queryOptions({
    queryKey: ["environment"],
    queryFn: getEnvironmentConfig,
    staleTime: Infinity,
  });
}

async function getEnvironmentConfig(): Promise<Environment> {
  try {
    const response = await doFetch(`${apiServer ?? ""}/api/config/env`);
    const environment = (await response.json()) as Environment;

    if (environment && environment.features === undefined) {
      environment.features = [];
    }

    const enrichedEnvironment = {
      ...environment,
      hasFeature: (feature: Feature | Array<Feature> | undefined) => {
        return hasFeature(environment.features, feature);
      },
    };

    return enrichedEnvironment;
  } catch (err) {
    // Log and return a safe default so the app can render during local development
    // even when the backend (or proxy) is not available.
    // eslint-disable-next-line no-console
    console.warn("getEnvironmentConfig: failed to fetch /api/config/env, using defaults:", err);

    const defaultEnv: Environment = {
      awsExports: {},
      defaults: { panels: "" },
      paypal: { clientId: "" },
      stripe: { id: "" },
      google: { apiKey: "" },
      ipgeolocation: { url: "" },
      features: [],
      hasFeature: () => false,
    };

    return defaultEnv;
  }
}

type UseEnvironmentConfigOptions = {
  queryConfig?: QueryConfig<typeof getEnvironmentConfigQueryOptions>;
};

export function useEnvironmentConfig({ queryConfig }: UseEnvironmentConfigOptions = {}) {
  return useQuery({
    ...getEnvironmentConfigQueryOptions(),
    ...queryConfig,
  });
}

export type PayPalClientToken = { clientToken: string };

export function getPayPalClientTokenQueryOptions() {
  return queryOptions({
    queryKey: ["paypal-client-token"],
    queryFn: getPayPalClientToken,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export async function getPayPalClientToken(): Promise<PayPalClientToken> {
  const response = await doFetch(`${apiServer ?? ""}/api/orders/payments/paypal/client-token`);
  if (!response.ok) {
    throw new Error(`Failed to fetch PayPal client token: ${response.status}`);
  }

  return (await response.json()) as PayPalClientToken;
}

export function usePayPalClientToken({ queryConfig }: { queryConfig?: QueryConfig<typeof getPayPalClientTokenQueryOptions>; } = {}) {
  return useQuery({
    ...getPayPalClientTokenQueryOptions(),
    ...queryConfig,
  });
}